import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  CHART_OPTIONS,
  DEFAULT_DARK_THEME_BORDER_COLOR,
  DEFAULT_DARK_THEME_TEXT_COLOR,
  DEFAULT_FONT_FAMILY,
  DEFAULT_LIGHT_THEME_BORDER_COLOR,
  DEFAULT_LIGHT_THEME_TEXT_COLOR,
} from '@app-core/constants/constants';
import { DateService } from '@app-core/services/date/date.service';
import { IncidentRatioTrend, PreviousCoachingSession } from '@app-reports/models/reports.model';
import { TranslateService } from '@ngx-translate/core';
import { Chart, ChartConfiguration } from 'chart.js/auto';
import { Subject } from 'rxjs';
import * as momentTz from 'moment-timezone';
import { CoachingTimelineService } from '@app-reports/services/coaching-timeline.service';

@Component({
  selector: 'app-coaching-timeline',
  templateUrl: './coaching-timeline.component.html',
  styleUrls: ['./coaching-timeline.component.scss'],
})
export class CoachingTimelineComponent implements OnChanges, OnDestroy {
  @Input() public loader: boolean = true;
  @Input() public currentTheme: string;
  @Input() public currentMetricUnit: string;
  @Input() public currenntTimeZone: string;
  @Input() public currentDateFormat: string;
  @Input() public previousCoachingSession: PreviousCoachingSession[];
  @Input() public eventsPer100Miles: IncidentRatioTrend[];

  public showChart: boolean;
  public chart: Chart;

  private unsubscribeOnChanges: Subject<void> = new Subject<void>();
  private unsubscribeOnDestroy: Subject<void> = new Subject<void>();

  constructor(
    public translate: TranslateService,
    private dateService: DateService,
    private elementRef: ElementRef,
    private coachingTimelineService: CoachingTimelineService
  ) {
    this.translate.onLangChange.subscribe(() => {
      if (!this.loader) {
        this.generateChart();
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.currentTheme) {
      this.changeTheme();
    } else {
      if (!this.loader) {
        this.generateChart();
      }
    }
  }

  private generateChart() {
    if (this.chart) {
      this.chart.destroy();
    }

    if (!this.eventsPer100Miles?.length && !this.previousCoachingSession?.length) {
      this.showChart = false;
      return;
    }

    const ctx: any = this.elementRef.nativeElement.querySelector(`#coachingTimelineChart`);
    const originalEventsPer100Miles = this.eventsPer100Miles;
    const { extendedEventsPer100Miles, coachingSessionChartData } = this.coachingTimelineService.getCoachingTimelineData(
      originalEventsPer100Miles,
      this.previousCoachingSession
    );

    const labels = extendedEventsPer100Miles.map((item, index) => {
      if (index === extendedEventsPer100Miles.length - 1) {
        return [this.dateService.getMonthAndYear(item.date), this.translate.instant('Present')];
      }
      return this.dateService.getMonthAndYear(item.date);
    });

    const data = extendedEventsPer100Miles.map((item) => {
      return item.incidentRatio;
    });

    const translate = this.translate.instant.bind(this.translate);
    const annotations = this.createAnnotations(translate, coachingSessionChartData);

    this.chart = new Chart(ctx, <ChartConfiguration>{
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            data: data,
            borderColor: '#9A66FF',
            backgroundColor: '#5600FD',
            fill: false,
          },
        ],
      },
      options: {
        ...CHART_OPTIONS,
        plugins: {
          ...CHART_OPTIONS.plugins,
          annotation: {
            clip: false,
            annotations: annotations,
          },
          tooltip: {
            usePointStyle: true,
            bodyFont: {
              family: DEFAULT_FONT_FAMILY,
            },
            callbacks: {
              title: () => {
                return '';
              },
              label: (context) => {
                const value = context.raw;
                const text = translate('Incidents');

                return [` ${text} : ${value} `];
              },
            },
          },
        },
      },
    });
    this.assignAxisTexts();
    this.chart.update();
    this.changeTheme();
    this.showChart = true;
    this.loader = false;
  }

  private createAnnotations(translate, annotationChartData) {
    const text = translate('Session');
    return annotationChartData.map((date) => ({
      ...CHART_OPTIONS.plugins.annotation.annotations,
      value: date.value,
      enter({ element }, _event) {
        element.label.options.display = true;
        return true;
      },
      leave({ element }, _event) {
        element.label.options.display = false;
        return true;
      },
      label: {
        display: false,
        enabled: true,
        color: ['white', 'white'],
        content: [
          `${text} ${date.coachingSessionId}`,
          `${momentTz.tz(date.date, this.currenntTimeZone).format(this.currentDateFormat.slice(0, 10))}`,
        ],
        font: [{ size: 14, weight: 'bold', family: DEFAULT_FONT_FAMILY, lineHeight: 2 }, {}],
        position: 'start',
        textAlign: 'start',
        yAdjust: -3,
        cornerRadius: 4,
      },
    }));
  }

  private assignAxisTexts() {
    const yAxixTextMiles = this.translate.instant('fleetStatsIncidentsPer') + ' ' + this.translate.instant('MILES');
    const yAxisTextKiloMeters = this.translate.instant('fleetStatsIncidentsPer') + ' ' + this.translate.instant('KILOMETERS');

    this.chart.options.scales.y['title'] = {
      ...this.chart.options.scales.y['title'],
      text:
        this.currentMetricUnit === 'Miles'
          ? `${this.translate.instant(yAxixTextMiles.toUpperCase())}`
          : `${this.translate.instant(yAxisTextKiloMeters.toUpperCase())}`,
    };

    const xAxisText = this.translate.instant('TIME RANGE');
    this.chart.options.scales.x['title'] = {
      ...this.chart.options.scales.x['title'],
      text: `${xAxisText}`,
    };
  }

  public changeTheme() {
    if (this.chart) {
      if (this.currentTheme === 'light') {
        this.chart.options.plugins.legend.labels.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.y.grid.color = DEFAULT_LIGHT_THEME_BORDER_COLOR;
        this.chart.options.scales.x.grid.color = DEFAULT_LIGHT_THEME_BORDER_COLOR;
        this.chart.options.scales.x.ticks.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.y.ticks.color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.y['title'].color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
        this.chart.options.scales.x['title'].color = DEFAULT_LIGHT_THEME_TEXT_COLOR;
      } else {
        this.chart.options.plugins.legend.labels.color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.y.grid.color = DEFAULT_DARK_THEME_BORDER_COLOR;
        this.chart.options.scales.x.grid.color = DEFAULT_DARK_THEME_BORDER_COLOR;
        this.chart.options.scales.x.ticks.color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.y.ticks.color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.y['title'].color = DEFAULT_DARK_THEME_TEXT_COLOR;
        this.chart.options.scales.x['title'].color = DEFAULT_DARK_THEME_TEXT_COLOR;
      }
      this.chart.update();
    }
  }

  public ngOnDestroy() {
    this.unsubscribeOnChanges.next();
    this.unsubscribeOnChanges.complete();

    this.unsubscribeOnDestroy.next();
    this.unsubscribeOnDestroy.complete();
  }
}
